/**
 * LeadStatusBadge.tsx — colour-coded pill for the 13-value `LeadStatus`.
 *
 * The shared `StatusBadge` is typed to the Health `SourceStatus` (4 values);
 * Leads has its own frozen 13-state machine, so this is a sibling badge with
 * the SAME visual language and the SAME WCAG posture: colour is paired with a
 * text label (i18n) so status is never conveyed by colour alone (WCAG 1.4.1),
 * and an out-of-domain / unknown value degrades to a neutral grey pill rather
 * than crashing or mis-colouring (brief: render edge states sensibly).
 *
 * Each status maps to a shared `Badge` tone.
 */

import { useTranslation } from 'react-i18next';
import { isLeadStatus, type LeadStatus } from '../../api/types';
import { Badge, type BadgeTone } from '../../components/Badge';

// LeadStatus → semantic tone. Grouped by funnel meaning so the colour reads
// consistently with the derived funnel_stage bucket.
const STATUS_TONE: Record<LeadStatus, BadgeTone> = {
  new: 'neutral',
  pulled: 'neutral',
  contacted: 'neutral',
  engaged: 'neutral',
  collecting: 'degraded',
  docs_complete: 'degraded',
  handed_off: 'ok',
  closed_known: 'ok',
  closed_unknown: 'down',
  opted_out: 'down',
  undeliverable: 'down',
  consent_unverified: 'unknown',
  quarantine: 'unknown',
};

export interface LeadStatusBadgeProps {
  /** A LeadStatus, or any string (defensive: unknown values render neutrally). */
  status: string;
  className?: string;
}

export const LeadStatusBadge = ({ status, className = '' }: LeadStatusBadgeProps) => {
  const { t } = useTranslation();
  const known = isLeadStatus(status);
  const tone: BadgeTone = known ? STATUS_TONE[status] : 'unknown';
  // Known statuses get their localised label; an out-of-domain value is shown
  // verbatim behind a generic "unknown status" wrapper so an operator can see
  // exactly what the backend sent without the UI guessing a colour.
  const label = known ? t(`leadStatus.${status}`) : t('leads.statusUnknown', { value: status });

  return (
    <Badge
      tone={tone}
      data-testid="lead-status-badge"
      data-status={status}
      data-known={known ? 'true' : 'false'}
      className={className}
    >
      {label}
    </Badge>
  );
};
